import { useEffect, useState, useRef } from "react";
import { Bell, X, Check, AlertCircle, Info, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // 401 expected for anonymous; surface other failures
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("Notifications poll failed:", e.message);
      }
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      setUnread(u => Math.max(0, u - 1));
      setItems(prev => prev.map(it => it.id === n.id ? { ...it, read: true } : it));
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setItems(prev => prev.map(it => ({ ...it, read: true })));
  };

  const ICONS = { success: CheckCircle2, warning: AlertCircle, error: AlertCircle, info: Info };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(o => !o)}
        className="relative p-2 hover:bg-slate-800 rounded-md transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 bg-teal-500 text-slate-900 text-[10px] font-bold rounded-full w-4 h-4 grid place-items-center"
          >{unread > 9 ? "9+" : unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white text-slate-900 rounded-lg shadow-xl border border-slate-200 z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-semibold text-sm">Notifications</div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-teal-700 font-medium hover:underline" data-testid="mark-all-read-btn">Mark all read</button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">No notifications yet.</div>
            ) : items.map(n => {
              const Icon = ICONS[n.kind] || Info;
              const tone = n.kind === "success" ? "text-teal-600" : n.kind === "warning" ? "text-amber-600" : n.kind === "error" ? "text-red-600" : "text-slate-500";
              return (
                <button
                  key={n.id}
                  onClick={() => markRead(n)}
                  className={cn("w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3", !n.read && "bg-teal-50/40")}
                  data-testid={`notification-item-${n.id}`}
                >
                  <Icon size={16} className={cn("mt-0.5 flex-shrink-0", tone)} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{n.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5 truncate">{n.message}</div>
                    <div className="text-[10px] text-slate-400 mt-1 font-mono">{new Date(n.created_at).toLocaleString("en-IN")}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-teal-500 mt-2 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
